import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LoadingPage from "../components/Loader";
import {
  FaEdit,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaWallet,
  FaHistory,
  FaSignOutAlt,
  FaQuoteLeft,
  FaCheckCircle,
  FaTimes,
  FaUpload,
  FaCrown,
  FaUniversity,
  FaExchangeAlt,
} from "react-icons/fa";
import { useUniversities } from "../context/universitiesContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CurrencyConverterModal from '../components/currency/CurrencyConverterModal';
import { nairaToBytes, formatBytes } from '../utils/currency';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [nearestLandmark, setNearestLandmark] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showConverter, setShowConverter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const { universities } = useUniversities();
  const [universityLandmarks, setUniversityLandmarks] = useState([]);
  // const [landmarksLoading] = useState(false);

  // Utility function to safely get university name
  const getUniversityName = (university, universities, fallback = "University") => {
    if (!university) return "No University Selected";
    if (typeof university === "object" && university.name) {
      return university.name;
    }
    if (typeof university === "string") {
      return universities.find((uni) => uni._id === university)?.name || fallback;
    }
    return fallback;
  };

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await axios.get(
            "https://mongobyte-hc73.onrender.com/api/v1/users/getProfile",
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setUser(response.data.user);
          setBio(response.data.user.bio || "");
          setLocation(response.data.user.location || "");
          setNearestLandmark(response.data.user.nearestLandmark || "");
          setSelectedUniversity(
            typeof response.data.user.university === "object"
              ? response.data.user.university?._id || ""
              : response.data.user.university || ""
          );
          setLoading(false);
        } catch (error) {
          console.error("Profile fetch error:", error);
          setError("Failed to fetch user profile. Please try again.");
          setLoading(false);
        }
      } else {
        setError("No user token found. Please log in.");
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchLandmarks = async () => {
      if (!selectedUniversity) {
        setUniversityLandmarks([]);
        return;
      }
      try {
        const response = await axios.get(
          `https://mongobyte-hc73.onrender.com/api/v1/universities/${selectedUniversity}/landmarks`
        );
        setUniversityLandmarks(response.data.landmarks || []);
      } catch (error) {
        setUniversityLandmarks([]);
      }
    };
    fetchLandmarks();
  }, [selectedUniversity]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("byteUser");
    navigate("/");
  };

  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) return;
    try {
      const response = await axios.post(
        "https://mongobyte-hc73.onrender.com/api/v1/users/upload",
        { image: selectedImage },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data.url;
    } catch (error) {
      console.error("Image upload error:", error);
      throw error;
    }
  };

  const updateUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setUpdateLoading(true);
    try {
      let imageUrl = user?.imageUrl;
      if (selectedImage) {
        imageUrl = await handleImageUpload();
      }

      const profileData = {};
      if (imageUrl !== user?.imageUrl) profileData.imageUrl = imageUrl;
      if (bio !== user?.bio) profileData.bio = bio;
      if (location !== user?.location) profileData.location = location;
      if (nearestLandmark !== user?.nearestLandmark) profileData.nearestLandmark = nearestLandmark;
      const currentUniversity =
        typeof user?.university === "object" ? user?.university?._id : user?.university;
      if (selectedUniversity !== currentUniversity) profileData.university = selectedUniversity;

      if (Object.keys(profileData).length === 0) {
        setIsModalOpen(false);
        setUpdateLoading(false);
        return;
      }

      const response = await axios.put(
        "https://mongobyte-hc73.onrender.com/api/v1/users/updateProfile",
        profileData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
      }

      if (response.data.user) {
        setUser(response.data.user);
        localStorage.setItem("byteUser", JSON.stringify(response.data.user));
      } else {
        setUser((prevUser) => ({ ...prevUser, ...profileData }));
      }

      setIsModalOpen(false);
      setUpdateLoading(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error("Profile update error:", error);
      setError("Failed to update profile. Please try again.");
      setUpdateLoading(false);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-red-50 to-red-100">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md"
        >
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-600 text-lg font-semibold mb-4">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 py-6 px-4 pt-20 md:pt-24 pb-24 md:pb-6">
      <CurrencyConverterModal isOpen={showConverter} onClose={() => setShowConverter(false)} />
      <div className="max-w-4xl mx-auto space-y-6">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -100 }}
              className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2"
            >
              <FaCheckCircle />
              <span>Profile updated successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="max-w-6xl mx-auto">
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative rounded-[28px] overflow-hidden shadow-2xl mb-8 bg-white border border-gray-100"
                  >
                    {/* Cover image */}
                    <img
                      src={
                        user?.imageUrl ||
                        "https://res.cloudinary.com/dol47ucmj/image/upload/v1729928426/jm9dfybhu5pqqevrhyke.jpg"
                      }
                      alt="Profile cover"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {/* Taller blur area with smooth transition */}
                    <div
                      className="absolute -bottom-28 left-0 right-0 top-[40%] z-0"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%)",
                        backdropFilter: "blur(40px) brightness(0.7)",
                        WebkitBackdropFilter: "blur(50px) brightness(0.7)",
                        /* Smooth the top edge to avoid a harsh line */
                        maskImage:
                          "linear-gradient(to bottom, rgba(0,0,0,0) 0%,rgba(0,0,0,0.8) 12%, rgba(0,0,0,1) 25%)",
                        WebkitMaskImage:
                          "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 35%)",
                        /* Add a subtle inset shadow to further feather the top boundary */
                        boxShadow: "0 -40px 50px -20px rgba(0,0,0,0.5) inset",
                        pointerEvents: "none",
                        mixBlendMode: "multiply",
                        transform: "translateZ(0)",
                      }}
                    />
        
                    {/* VIP badge */}
                    {user?.role === "VIP" && (
                      <div className="absolute top-4 left-4 z-10 bg-cheese text-crust rounded-full p-2 shadow-lg">
                        <FaCrown className="text-sm" />
                      </div>
                    )}
        
                    {/* Content */}
                    <div className="-mx-4 relative z-30 h-[420px] sm:h-[460px] md:h-[500px] p-6 px-4 sm:p-8 flex flex-col">
                      {/* Header chip */}
                      <div className="flex items-center justify-center z-40">
                        <div className="px-3 py-1 rounded-full text-xs sm:text-sm font-medium text-black bg-yellow-400/20 backdrop-blur-md shadow-md ring-1 ring-white/40">
                          <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 mr-2"></span>
                          Byte's Foodie Card
                          <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 ml-2"></span>
                        </div>
                      </div>
                      {/* Centered name and bio */}
                      <div className="mt-auto text-center text-gray-800 z-40">
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight drop-shadow-md">
                          {user?.firstName} {user?.lastName}
                        </h1>
                        {/* Mobile user info moved below username in footer */}
        
                        {user?.bio && (
                          <div className="mt-3 mx-auto max-w-lg rounded-2xl p-3 text-left">
                            <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-xs text-white/80 mb-2">
                              <FaQuoteLeft className="text-cheese" />
                              Bio
                            </div>
                            <p className="text-white text-base sm:text-lg italic">
                              "{user.bio}"
                            </p>
                            <span className="text-md font-secondary text-white">
                              @{user?.username}
                            </span>
                          </div>
                        )}
                      </div>
        
                      {/* Footer controls */}
                      <div className="mt-6 flex items-start gap-2 sm:flex-row sm:items-center justify-between z-40">
                        <div className="mx-4">
                          <div className="flex items-center gap-3 rounded-full bg-white/20 backdrop-blur-sm shadow-sm px-3 py-1.5  ring-1 ring-gray-200/50 text-white">
                            <img
                              src={
                                user?.imageUrl ||
                                "https://res.cloudinary.com/dol47ucmj/image/upload/v1729928426/jm9dfybhu5pqqevrhyke.jpg"
                              }
                              alt="Avatar"
                              className="w-8 h-8 rounded-full object-cover"
                            />
                            <p className="text-xl text-white">{user?.orderItems || 0}</p>
                          </div>
                        </div>
        
                        {/* Mobile-only info below username, left-aligned with icons */}
                        <div className="sm:hidden mt-1 mr-4 space-y-2 text-sm text-white">
                          {user?.email && (
                            <div className="flex items-center gap-2 leading-tight break-words">
                              <FaEnvelope className="text-cheese text-xs flex-shrink-0" />
                              <span>{user.email}</span>
                            </div>
                          )}
                          {user?.phoneNumber && (
                            <div className="flex items-center gap-2 leading-tight">
                              <FaPhone className="text-cheese text-xs flex-shrink-0" />
                              <span>{user.phoneNumber}</span>
                            </div>
                          )}
                        </div>
        
                        {/* Desktop user info - hidden on mobile */}
                        <div className="hidden sm:flex flex-col items-end gap-2">
                          {user?.email && (
                            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100/30 backdrop-blur-sm shadow-sm px-3 py-1.5 ring-1 ring-gray-200/50 text-white">
                              <FaEnvelope className="text-cheese" />
                              <span className="text-sm">{user.email}</span>
                            </div>
                          )}
                          {user?.phoneNumber && (
                            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100/30 backdrop-blur-sm shadow-sm px-3 py-1.5 ring-1 ring-gray-200/50 text-white">
                              <FaPhone className="text-cheese" />
                              <span className="text-sm">{user.phoneNumber}</span>
                            </div>
                          )}
                          <div className="inline-flex items-center gap-2 rounded-full bg-cheese/90 text-white px-3 py-1.5 shadow">
                            <FaUniversity />
                            <span className="text-xs sm:text-sm font-semibold">
                              #
                              {user?.university
                                ? getUniversityName(
                                    user.university,
                                    universities,
                                    "University"
                                  )
                                : "None"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  {/* Edit Profile Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={openModal}
                      className="w-full bg-crust text-white font-semibold py-4 px-6 rounded-2xl shadow-lg flex items-center justify-center gap-3 text-lg hover:bg-gray-900 transition-all"
                    >
                      <FaEdit className="text-cheese" />
                      Edit Profile
                    </motion.button>
                  </motion.div>


                  {/* Redesigned Stats Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-8 mb-8 shadow-xl border border-gray-100"
                  >
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">Account Overview</h3>
                      <p className="text-gray-600">Your profile statistics at a glance</p>
                    </div>
                    
                    <div className="space-y-6">
                      {/* Balance Row */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl p-6 text-gray-900"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="bg-white/20 p-3 rounded-full">
                              <FaWallet className="text-2xl text-gray-900" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">Account Balance</p>
                              <p className="text-3xl font-bold">₦{(user?.byteBalance || 0).toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Orders and University Row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                          className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-red-500"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-red-100 p-3 rounded-full">
                              <FaHistory className="text-xl text-red-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Total Orders</p>
                              <p className="text-2xl font-bold text-gray-900">{user?.orderItems || 0}</p>
                            </div>
                          </div>
                        </motion.div>

                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                          className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-blue-500"
                        >
                          <div className="flex items-center gap-4">
                            <div className="bg-blue-100 p-3 rounded-full">
                              <FaUniversity className="text-xl text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">University</p>
                              <p className="text-lg font-bold text-gray-900 truncate">
                                {user?.university
                                  ? getUniversityName(user.university, universities, "University")
                                  : "Not Set"}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>


        
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl p-8 mb-8 shadow-xl border border-gray-200"
                  >
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center gap-3">
                      <FaMapMarkerAlt className="text-pepperoni" />
                      Delivery Information
                    </h3>
                    <div className="grid lg:grid-cols-2 gap-6">
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-cheese"
                      >
                        <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                          Current Location
                        </h4>
                        <p className="text-gray-700 font-medium">
                          {user?.location || "Please set your location"}
                        </p>
                        {!user?.location && (
                          <p className="text-pepperoni text-sm mt-2">
                            Add location for faster delivery; click on edit profile!
                          </p>
                        )}
                      </motion.div>
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-cheese"
                      >
                        <h4 className="font-bold text-ray-700 mb-3 flex items-center gap-2">
                          Nearest Landmark
                        </h4>
                        <p className="text-gray-700 font-medium">
                          {user?.nearestLandmark || "Add a landmark"}
                        </p>
                        {!user?.nearestLandmark && (
                          <p className="text-pepperoni text-sm mt-2">
                            Help us locate you easily; click on edit profile!
                          </p>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>
        
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-3xl p-8 mb-8 shadow-xl border border-gray-200"
                  >
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center gap-3">
                      <FaUniversity className="text-pepperoni" />
                      University Information
                    </h3>
                    <div className="flex flex-col items-center text-center">
                      {user?.university ? (
                        <>
                          <div className="bg-gray-50 rounded-full w-24 h-24 flex items-center justify-center mb-4 shadow-sm">
                            <FaUniversity className="text-4xl text-pepperoni" />
                          </div>
                          <h4 className="text-xl font-bold text-gray-800 mb-2">
                            {getUniversityName(
                              user.university,
                              universities,
                              "University"
                            )}
                          </h4>
                          <p className="text-gray-600 mb-6">
                            You'll see restaurants available at your university
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={openModal}
                            className="flex items-center gap-2 bg-cheese text-crust px-6 py-3 rounded-xl font-semibold shadow-md hover:bg-yellow-400 transition-colors"
                          >
                            <FaExchangeAlt />
                            Change University
                          </motion.button>
                        </>
                      ) : (
                        <>
                          <div className="bg-gray-50 rounded-full w-24 h-24 flex items-center justify-center mb-4 shadow-sm">
                            <FaUniversity className="text-4xl text-gray-400" />
                          </div>
                          <h4 className="text-xl font-bold text-gray-800 mb-2">
                            No University Selected
                          </h4>
                          <p className="text-gray-600 mb-6">
                            Setting your university helps us show you relevant
                            restaurants
                          </p>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={openModal}
                            className="flex items-center gap-2 bg-pepperoni text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:bg-red-700 transition-colors"
                          >
                            <FaUniversity />
                            Set University
                          </motion.button>
                        </>
                      )}
                    </div>
                  </motion.div>
        
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col lg:flex-row gap-4"
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate("/user/orderhistory")}
                      className="flex-1 bg-pepperoni text-white font-semibold py-4 px-6 rounded-2xl shadow-lg flex items-center justify-center gap-3 text-lg hover:bg-red-700 transition-all"
                    >
                      <FaHistory />
                      Order History
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleLogout}
                      className="flex-1 bg-gray-200 text-gray-800 font-semibold py-4 px-6 rounded-2xl shadow-lg flex items-center justify-center gap-3 text-lg hover:bg-gray-300 transition-all"
                    >
                      <FaSignOutAlt />
                      Sign Out
                    </motion.button>
                  </motion.div>
                </div>

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto"
              >
                <div className="bg-crust p-6 text-white relative">
                  <button
                    onClick={closeModal}
                    className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-all"
                  >
                    <FaTimes />
                  </button>
                  <h2 className="text-2xl font-bold text-cheese">Edit Profile</h2>
                  <p className="text-white/80 text-sm">Update your information</p>
                </div>
                <div className="p-6 space-y-6">
                  <div>
                    <label className=" text-sm font-semibold text-crust mb-2 items-center gap-2">
                      <FaUniversity className="text-pepperoni" />
                      University
                    </label>
                    <select
                      value={selectedUniversity}
                      onChange={(e) => {
                        setSelectedUniversity(e.target.value);
                        setNearestLandmark("");
                      }}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent hover:border-cheese transition-colors"
                    >
                      <option value="">Select your university</option>
                      {universities.map((university) => (
                        <option key={university._id} value={university._id}>
                          {university.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-crust mb-3">Profile Picture</label>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <img
                          src={
                            selectedImage ||
                            user?.imageUrl ||
                            "https://res.cloudinary.com/dol47ucmj/image/upload/v1729928426/jm9dfybhu5pqqevrhyke.jpg"
                          }
                          alt="Preview"
                          className="w-20 h-20 rounded-full object-cover border-4 border-cheese"
                        />
                        {selectedImage && (
                          <div className="absolute -top-2 -right-2 bg-pepperoni text-white p-1 rounded-full">
                            <FaCheckCircle className="text-xs" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                          id="profile-upload"
                        />
                        <label
                          htmlFor="profile-upload"
                          className="cursor-pointer bg-orange-50 hover:bg-orange-100 text-crust font-medium py-3 px-4 rounded-xl border-2 border-dashed border-cheese hover:border-pepperoni transition-all flex items-center justify-center gap-2"
                        >
                          <FaUpload />
                          Choose Photo
                        </label>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-crust mb-2">Bio</label>
                    <textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent resize-none hover:border-cheese transition-colors"
                      rows="4"
                      placeholder="Tell us about yourself..."
                      maxLength={200}
                    />
                    <div className="text-xs text-gray-500 mt-1 text-right">{bio.length}/200 characters</div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-crust mb-2">Location</label>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent hover:border-cheese transition-colors"
                      placeholder="Your delivery location..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-crust mb-2">Nearest Landmark</label>
                    {selectedUniversity && universityLandmarks.length > 0 ? (
                      <select
                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent hover:border-cheese transition-colors"
                        value={nearestLandmark}
                        onChange={(e) => setNearestLandmark(e.target.value)}
                      >
                        <option value="">Select a landmark...</option>
                        {universityLandmarks.map((lm, idx) => (
                          <option key={idx} value={lm}>{lm}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={nearestLandmark}
                        onChange={(e) => setNearestLandmark(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent hover:border-cheese transition-colors"
                        placeholder="Nearby landmark..."
                      />
                    )}
                  </div>
                  <div className="flex gap-3 pt-4">
                    {updateLoading ? (
                      <LoadingPage />
                    ) : (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={updateUserProfile}
                          className="flex-1 bg-cheese text-crust font-bold py-4 px-6 rounded-xl shadow-lg hover:bg-yellow-400 transition-all flex items-center justify-center gap-2"
                        >
                          <FaCheckCircle />
                          Save
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={closeModal}
                          className="px-6 py-4 text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-semibold transition-all rounded-xl flex items-center gap-2"
                        >
                          <FaTimes />
                          Cancel
                        </motion.button>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Profile;